import React, { useContext, useEffect, useState } from 'react';
import { LoaderAnimation, RocketAnimation } from 'src/components';
import { Connecting, IceCreamImage } from 'src/assets/images';
import {
  AcquiringChannelFlags,
  FicaStatus,
  SalesforceContext,
} from 'src/context/salesforce-context';
import TealiumEvents from 'src/helpers/tealium-events';
import NavigationFooter from 'src/components/navigation-footer/navigation-footer';
import { vfsHomeUrl } from 'src/common';
import './fica-processing.scss';

export default function FicaProcessing() {
  const {
    refreshFicaStatus,
    getFicaStatus,
    isLoadingFromSessionStorage,
    initFicaPollingStartTime,
    merchant,
    uniqueTerminals,
    customerType,
    isResumedSession,
    lead,
    leadId,
    dealerId,
  } = useContext(SalesforceContext);
  const [redirect, setRedirect] = useState();
  const POLLING_DELAY_SEC = 30;
  const orderReference = merchant?.opportunity?.reference_no || '';

  const getTealiumParameters = () => {
    const businessType = merchant.account.business_type;
    const monthlyCardTurnover = lead.monthlyCardTurnover;

    const deviceNameList = [];
    const quantityList = [];
    const priceList = [];
    let transactionTotal = 0;
    let orderedRentalDevice = false;
    let orderedOnceOffFeeDevice = false;

    merchant.opportunity &&
      Object.values(merchant.opportunity.deliveries)
        .filter((delivery) => delivery.number_of_devices > 0)
        .forEach((delivery) => {
          deviceNameList.push(delivery.device_name);
          quantityList.push(delivery.number_of_devices);
          priceList.push(delivery.price);
          transactionTotal = transactionTotal + delivery.price * delivery.number_of_devices;
          const acquiringChannel = uniqueTerminals[delivery.device_id].acquiring_channel;
          orderedRentalDevice =
            orderedRentalDevice ||
            acquiringChannel === AcquiringChannelFlags.rentOnlineFicaOnline ||
            acquiringChannel === AcquiringChannelFlags.zeroRentOnlineFicaOnline;
          orderedOnceOffFeeDevice =
            orderedOnceOffFeeDevice ||
            acquiringChannel === AcquiringChannelFlags.buyOnlineFicaOnline;
        });

    let transactionPaymentType = [];
    orderedRentalDevice && transactionPaymentType.push('monthly rental');
    orderedOnceOffFeeDevice && transactionPaymentType.push('once-off payment');

    return {
      businessType: businessType,
      productNameList: deviceNameList,
      quantityList: quantityList,
      priceList: priceList,
      isOtpUsed: isResumedSession,
      transactionId: leadId,
      customerType: customerType,
      customerId: dealerId,
      transactionPaymentType: transactionPaymentType,
      transactionTotal: transactionTotal,
      monthlyCardTurnover: monthlyCardTurnover,
    };
  };

  useEffect(() => {
    if (
      !isLoadingFromSessionStorage &&
      uniqueTerminals &&
      Object.values(uniqueTerminals).length > 0
    ) {
      const ficaStatus = getFicaStatus();
      if (ficaStatus === FicaStatus.inProgress) {
        initFicaPollingStartTime();

        const interval = setInterval(async () => {
          const ficaStatus = getFicaStatus();
          if (ficaStatus === FicaStatus.nonStarted || ficaStatus === FicaStatus.inProgress) {
            await refreshFicaStatus();
          } else {
            clearInterval(interval);
          }
        }, POLLING_DELAY_SEC * 1000);
        return () => clearInterval(interval);
      }

      switch (ficaStatus) {
        case FicaStatus.pending:
          TealiumEvents.ficaPendingPageLoad(getTealiumParameters());
          TealiumEvents.ficaInProgressJourneyEnd();
          return;
        case FicaStatus.unsuccessful:
          TealiumEvents.ficaFailurePageLoad(getTealiumParameters());
          TealiumEvents.ficaInProgressJourneyEnd();
          return;
        case FicaStatus.successful:
          TealiumEvents.ficaSuccessfulPageLoad(getTealiumParameters());
          TealiumEvents.ficaInProgressJourneyEnd();
          return;
      }
    }
  }, [isLoadingFromSessionStorage, getFicaStatus(), uniqueTerminals]);

  useEffect(() => {
    if (
      !isLoadingFromSessionStorage &&
      uniqueTerminals &&
      Object.values(uniqueTerminals).length > 0
    ) {
      TealiumEvents.ficaInProgressPageLoad(getTealiumParameters());
      TealiumEvents.ficaInProgressJourneyStart();
    }
  }, [isLoadingFromSessionStorage, uniqueTerminals]);

  const handleNext = () => {
    let redirectLink = vfsHomeUrl;
    TealiumEvents.ficaClickDone(redirectLink, customerType);
    setRedirect(redirectLink);
  };

  const getOrderSubtext = () => {
    const lines =
      (merchant.opportunity &&
        Object.values(merchant.opportunity.deliveries)
          .filter((delivery) => delivery.number_of_devices > 0)
          .map((delivery) => `${delivery.device_name} x${delivery.number_of_devices}`)) ||
      [];
    return (
      <table className="table">
        <tbody>
          {lines.map((line, index) => (
            <tr key={index}>
              <td> {index === 0 ? 'Order:' : ''}</td>
              <td>{line}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const screenContent = {
    inProgress: {
      graphic: <LoaderAnimation height={125} width={125} />,
      style: 'in-progress',
      heading: `Thank you for your application ${merchant?.contact?.first_names}`,
      text: 'We are processing the FICA application, we will give you feedback within + - 5 minutes.',
    },
    successful: {
      graphic: <RocketAnimation />,
      style: 'successful',
      heading: 'Application successful!',
      text: "Please process the customer's payment.",
      subtext: getOrderSubtext(),
    },
    unsuccessful: {
      graphic: <img src={IceCreamImage} />,
      style: 'unsuccessful',
      heading: 'Application unsuccessful',
      text: "If you'd like assistance please contact the call centre.",
    },
    pending: {
      graphic: <img src={Connecting} />,
      style: 'pending',
      heading: 'FICA Pending',
      text:
        'We have referred the application for further review, please ' +
        'return to the store at a later stage to complete your application.',
    },
  };

  if (redirect) {
    window.location.href = redirect;
    return null;
  }

  const ficaStatus = getFicaStatus();
  const screenContentCurrent = screenContent[ficaStatus];

  return (
    <div className="fica-processing">
      <div className={`fica-container ${screenContentCurrent.style}`}>
        <div className="fica-graphic has-text-centered">{screenContentCurrent.graphic}</div>

        <div className="fica-heading heading-four has-text-centered">
          {screenContentCurrent.heading}
        </div>

        <div className="fica-text-block">
          <div className="fica-text has-text-centered">{screenContentCurrent.text}</div>

          {screenContentCurrent.subtext && (
            <div className="fica-text has-text-centered">{screenContentCurrent.subtext}</div>
          )}
        </div>

        <div className="fica-reference-number has-text-centered">
          Order reference: {orderReference}
        </div>
      </div>

      <NavigationFooter
        nextButtonLabel="Done"
        onNext={handleNext}
        hideSaveForLater={true}
        hideBack={true}
        hideCancel={true}
        nextEnabled={ficaStatus !== FicaStatus.inProgress}
        automationIdPrefix="Onboarding"
      />
    </div>
  );
}
